import { FC, useState } from 'react'
import { Size } from 'react-easy-crop'
import { useTranslation } from 'react-i18next'

import { Delete, Image, Upload } from '@mui/icons-material'
import { Avatar, Box, Button, IconButton, Skeleton, Stack, Tooltip } from '@mui/material'

import { usePostPublicFilesMutation } from '../../../api/core'
import ImageCropDialog from '../../../components/ImageCropDialog/ImageCropDialog'
import { displaySnackBar } from '../../../features/snackbar'
import { useAppDispatch } from '../../../hooks/storeHooks'
import { borderRadiusDefault } from '../../../themes/theme'
import { MAX_PUBLIC_UPLOAD_FILE_SIZE_BYTES } from '../../helpers/fileUploadLimits'
import { PublicFileType, UploadedFile } from '../../types/UploadedFile'

type InternalImageUploadProps = {
  // The uploaded image URL; '' when there is none.
  value: string
  // `file` is the upload's answer; it is missing when the image is removed.
  onChange: (url: string, file?: UploadedFile) => void
  type: PublicFileType
  label: string
  // Crops before uploading; the cropped image is a JPEG.
  crop?: { aspect?: number; shape?: 'rect' | 'round'; resize?: Size }
  removable?: boolean
  previewSize?: number
  // 'contain' shows the whole image at `previewSize` height, for images of any shape.
  previewVariant?: 'circular' | 'rounded' | 'contain'
  // Files at or above this size are rejected before uploading.
  maxBytes?: number
}

export const toCroppedFileName = (sourceName: string, mimeType: string) => {
  const baseName = sourceName.includes('.') ? sourceName.substring(0, sourceName.lastIndexOf('.')) : sourceName
  if (mimeType === 'image/jpeg') return `${baseName}.jpg`
  if (mimeType === 'image/png') return `${baseName}.png`
  return sourceName
}

export const InternalImageUpload: FC<InternalImageUploadProps> = ({
  value,
  onChange,
  type,
  label,
  crop,
  removable,
  previewSize = 96,
  previewVariant = 'circular',
  maxBytes = MAX_PUBLIC_UPLOAD_FILE_SIZE_BYTES,
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [uploadFiles, { isLoading: isUploading }] = usePostPublicFilesMutation()
  const [cropSource, setCropSource] = useState<{ src: string; name: string }>()

  const upload = async (file: File) => {
    const limit = Math.min(maxBytes, MAX_PUBLIC_UPLOAD_FILE_SIZE_BYTES)
    if (file.size >= limit) {
      dispatch(displaySnackBar({ message: t('internal-common:image_too_large', { size: limit / (1024 * 1024) }), severity: 'error', open: true }))
      return
    }
    try {
      const [uploaded] = await uploadFiles({ files: [file], type, route: 'public' }).unwrap()
      onChange(uploaded.url, uploaded)
      dispatch(displaySnackBar({ message: t('internal-common:image_uploaded'), severity: 'success', open: true }))
    } catch {
      dispatch(displaySnackBar({ message: t('internal-common:image_upload_failed'), severity: 'error', open: true }))
    }
  }

  const closeCrop = () => {
    if (cropSource) URL.revokeObjectURL(cropSource.src)
    setCropSource(undefined)
  }

  const handleFile = (file?: File) => {
    if (!file) return
    // `accept` is only a hint to the file picker, which can still be switched to any file.
    if (!file.type.startsWith('image/')) {
      dispatch(displaySnackBar({ message: t('internal-common:image_invalid_type'), severity: 'error', open: true }))
      return
    }
    if (crop) {
      setCropSource({ src: URL.createObjectURL(file), name: file.name })
    } else {
      upload(file)
    }
  }

  const handleCropApplied = (blob: Blob) => {
    const name = toCroppedFileName(cropSource?.name ?? 'image', blob.type)
    closeCrop()
    upload(new File([blob], name, { type: blob.type }))
  }

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      {isUploading ? (
        <Skeleton variant={previewVariant === 'contain' ? 'rounded' : previewVariant} width={previewSize} height={previewSize} />
      ) : previewVariant === 'contain' && value ? (
        <Box
          component="img"
          src={value}
          alt={label}
          sx={{ height: previewSize, maxWidth: previewSize * 3, objectFit: 'contain', borderRadius: borderRadiusDefault }}
        />
      ) : previewVariant === 'contain' ? (
        <Avatar variant="rounded" sx={{ width: previewSize, height: previewSize }}>
          <Image />
        </Avatar>
      ) : (
        <Avatar src={value || undefined} alt={label} variant={previewVariant} sx={{ width: previewSize, height: previewSize }} />
      )}
      <Button component="label" variant="outlined" startIcon={<Upload />} disabled={isUploading}>
        {t('internal-common:upload_image')}
        <input
          hidden
          type="file"
          accept="image/*"
          aria-label={label}
          onChange={(event) => {
            handleFile(event.target.files?.[0])
            event.target.value = ''
          }}
        />
      </Button>
      {removable && value && (
        <Tooltip title={t('internal-common:remove_image')}>
          <IconButton aria-label={t('internal-common:remove_image')} onClick={() => onChange('')} disabled={isUploading}>
            <Delete />
          </IconButton>
        </Tooltip>
      )}
      {cropSource && (
        <ImageCropDialog
          open
          imageSrc={cropSource.src}
          cropAspect={crop?.aspect}
          cropShape={crop?.shape}
          previewShape={crop?.shape === 'round' ? 'rounded' : 'rect'}
          resize={crop?.resize}
          onApply={handleCropApplied}
          onClose={closeCrop}
        />
      )}
    </Stack>
  )
}
